import { readErrorMessage } from '@Components/util/utils'
import { RootState } from '@AppState/redux-store'
import { GamesServiceAPI } from '@API/data-services/games-service'
import { DeleteConfirmationDialog } from '@Components/common/delete-confirmation-dialog'
import { WithPermissions } from '@Components/common/with-permissions'
import {
  AuthScopes,
  GamePromotionalMessage,
  ResponseError,
} from '@Generated/back-office-client/src'
import { Badge, Button, Flex, Skeleton, Text, TextArea, TextField } from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatInTimeZone } from 'date-fns-tz'
import { format, parseISO } from 'date-fns'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  DELETE_FAILED_MESSAGE,
  LOAD_FAILED_MESSAGE,
  MESSAGE_MAX_LENGTH,
  SAVE_FAILED_MESSAGE,
} from './constants'
import { PromotionalMessageFormData } from './types'
import { isLive, toCustomerEndOfDay, toCustomerMidnight, toEndDateInput } from './utils'
import './styles.scss'

const EMPTY_FORM: PromotionalMessageFormData = {
  id: null,
  message: '',
  activeFrom: null,
  activeTo: null,
}

const toFormData = (
  promotion: GamePromotionalMessage,
  timezone: string,
): PromotionalMessageFormData => ({
  id: promotion.id,
  message: promotion.message,
  activeFrom: promotion.activeFrom
    ? formatInTimeZone(new Date(promotion.activeFrom), timezone, 'yyyy-MM-dd')
    : null,
  activeTo: toEndDateInput(promotion.activeTo, timezone),
})

export const PromotionalMessageList = ({ gameId }: { gameId: string }) => {
  const { activeCustomerId, activeCustomerMetadata } = useSelector(
    (state: RootState) => state.users,
  )
  const timezone = activeCustomerMetadata?.timezone || 'America/New_York'
  const queryClient = useQueryClient()
  const queryKey = [`${activeCustomerId}-game-promotional-messages-${gameId}`]

  const [editingMessage, setEditingMessage] = useState<PromotionalMessageFormData | null>(null)
  const [deletingMessage, setDeletingMessage] = useState<GamePromotionalMessage | null>(null)
  const [messageError, setMessageError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const {
    data: messages,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey,
    refetchOnMount: 'always',
    queryFn: () => GamesServiceAPI.getGamePromotionalMessages(gameId),
    // A 4xx will answer the same way however many times it is asked, and each retry is another
    // backoff the operator waits through. Only a server error is worth a second try.
    retry: (failureCount, error) => {
      if (error instanceof ResponseError && error.response.status < 500) {
        return false
      }

      return failureCount < 1
    },
  })

  // The server decides overlap and the rest of the validation, so its own text is what the operator
  // sees. Rethrowing from a react-query onError only produces an unhandled rejection, so every
  // failure lands in the field error instead.
  const showFailure = async (error: unknown) => {
    setMessageError(await readErrorMessage(error, SAVE_FAILED_MESSAGE))
  }

  // No optimistic update: whether a window is legal is the server's answer, so a write refetches.
  const onWritten = () => {
    queryClient.invalidateQueries({ queryKey })
    setEditingMessage(null)
  }

  const createMutation = useMutation({
    mutationFn: (form: PromotionalMessageFormData) =>
      GamesServiceAPI.createGamePromotionalMessage(gameId, {
        message: form.message,
        activeFrom: toCustomerMidnight(form.activeFrom, timezone),
        activeTo: toCustomerEndOfDay(form.activeTo, timezone),
      }),
    onSuccess: onWritten,
    onError: showFailure,
  })

  const updateMutation = useMutation({
    mutationFn: (form: PromotionalMessageFormData) =>
      GamesServiceAPI.updateGamePromotionalMessage(gameId, form.id ?? '', {
        message: form.message,
        activeFrom: toCustomerMidnight(form.activeFrom, timezone),
        activeTo: toCustomerEndOfDay(form.activeTo, timezone),
      }),
    onSuccess: onWritten,
    onError: showFailure,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => GamesServiceAPI.deleteGamePromotionalMessage(gameId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      setDeletingMessage(null)
      setDeleteError(null)
    },
    // A delete can fail with no form open, so its own banner reports it rather than the field error.
    onError: async (error: unknown) => {
      setDeleteError(await readErrorMessage(error, DELETE_FAILED_MESSAGE))
      setDeletingMessage(null)
    },
  })

  const isSaving = createMutation.isPending || updateMutation.isPending

  const handleSave = () => {
    if (!editingMessage) {
      return
    }

    setMessageError(null)
    const copy = editingMessage.message.trim()
    if (!copy) {
      setMessageError('A message needs some copy.')
      return
    }
    if (copy.length > MESSAGE_MAX_LENGTH) {
      setMessageError(`A message cannot exceed ${MESSAGE_MAX_LENGTH} characters.`)
      return
    }
    // Both are yyyy-MM-dd, so a string compare is a date compare. The end day is inclusive, so a
    // window that starts and ends on the same day is legal.
    if (
      editingMessage.activeFrom &&
      editingMessage.activeTo &&
      editingMessage.activeTo < editingMessage.activeFrom
    ) {
      setMessageError('The end date cannot be before the start date.')
      return
    }

    if (editingMessage.id) {
      updateMutation.mutate(editingMessage)
    } else {
      createMutation.mutate(editingMessage)
    }
  }

  const startEditing = (form: PromotionalMessageFormData) => {
    setMessageError(null)
    setEditingMessage(form)
  }

  /** A new attempt drops the last one's errors, which otherwise outlive what they named. */
  const startDeleting = (promotion: GamePromotionalMessage) => {
    setMessageError(null)
    setDeleteError(null)
    setDeletingMessage(promotion)
  }

  const formatWindowDate = (date: Date | null | undefined, fallback: string) =>
    date ? formatInTimeZone(new Date(date), timezone, 'MMM d, yyyy') : fallback

  /** activeTo is stored exclusive, so the row names the last day the message actually shows. */
  const formatLastDay = (date: Date | null | undefined, fallback: string) => {
    const lastDay = toEndDateInput(date, timezone)
    if (!lastDay) {
      return fallback
    }

    // Already a customer-zone calendar date, so reformatting it needs no second zone conversion.
    return format(parseISO(lastDay), 'MMM d, yyyy')
  }

  if (isLoading) {
    return (
      <Skeleton loading={true}>
        <Flex direction="column" gap="2" data-testid="promotional-messages-loading-skeleton">
          <div className="skeleton-card" />
          <div className="skeleton-card" />
        </Flex>
      </Skeleton>
    )
  }

  return (
    <Flex direction="column" gap="3">
      <Flex justify="between" align="center">
        <Text size="3" weight="bold">
          Promotional message
        </Text>
        <WithPermissions requiredPermissions={AuthScopes.writegames}>
          <Button onClick={() => startEditing(EMPTY_FORM)}>Add message</Button>
        </WithPermissions>
      </Flex>

      {loadError && (
        <Text size="2" color="red" data-testid="promotional-messages-load-error">
          {LOAD_FAILED_MESSAGE}
        </Text>
      )}

      {deleteError && (
        <Text size="2" color="red" data-testid="promotional-message-delete-error">
          {deleteError}
        </Text>
      )}

      {!loadError && messages && messages.length > 0 ? (
        <Flex direction="column" gap="2">
          {messages.map((promotion) => (
            <Flex
              key={promotion.id}
              data-testid={`promotional-message-${promotion.id}`}
              className="promotional-message-row"
              gap="3"
              align="center">
              <Flex direction="column" gap="1" className="flex-1">
                <Flex gap="2" align="center">
                  <Text size="2">{promotion.message}</Text>
                  {isLive(promotion) && <Badge color="green">Live</Badge>}
                </Flex>
                <Flex gap="2" align="center">
                  <Text size="1" color="gray">
                    {formatWindowDate(promotion.activeFrom, 'Already live')}
                  </Text>
                  <Text size="1" color="gray">
                    to
                  </Text>
                  <Text size="1" color="gray">
                    {formatLastDay(promotion.activeTo, 'No end')}
                  </Text>
                </Flex>
              </Flex>
              <WithPermissions requiredPermissions={AuthScopes.writegames}>
                <Flex gap="2">
                  <Button
                    variant="outline"
                    onClick={() => startEditing(toFormData(promotion, timezone))}>
                    Edit
                  </Button>
                  <Button variant="outline" color="red" onClick={() => startDeleting(promotion)}>
                    Delete
                  </Button>
                </Flex>
              </WithPermissions>
            </Flex>
          ))}
        </Flex>
      ) : (
        // Only when the load succeeded — otherwise a failure reads as a game with no messages.
        !loadError && (
          <Text size="2" color="gray">
            No promotional messages scheduled
          </Text>
        )
      )}

      {editingMessage && (
        <Flex direction="column" gap="3" className="promotional-message-form">
          <Flex direction="column" gap="2">
            <label htmlFor="promotional-message-copy">
              <Text size="2" weight="medium">
                Message
              </Text>
            </label>
            <TextArea
              id="promotional-message-copy"
              value={editingMessage.message}
              onChange={(e) => setEditingMessage({ ...editingMessage, message: e.target.value })}
            />
            <Text
              size="1"
              color={editingMessage.message.length > MESSAGE_MAX_LENGTH ? 'red' : 'gray'}>
              {editingMessage.message.length} / {MESSAGE_MAX_LENGTH}
            </Text>
            {messageError && (
              <Text size="1" color="red">
                {messageError}
              </Text>
            )}
          </Flex>

          <Flex gap="3">
            <Flex direction="column" gap="2" className="flex-1">
              <label htmlFor="promotional-message-starts">
                <Text size="2" weight="medium">
                  Starts
                </Text>
              </label>
              <TextField.Root
                id="promotional-message-starts"
                type="date"
                value={editingMessage.activeFrom ?? ''}
                onChange={(e) =>
                  setEditingMessage({ ...editingMessage, activeFrom: e.target.value || null })
                }
              />
            </Flex>
            <Flex direction="column" gap="2" className="flex-1">
              <label htmlFor="promotional-message-ends">
                <Text size="2" weight="medium">
                  Ends
                </Text>
              </label>
              <TextField.Root
                id="promotional-message-ends"
                type="date"
                value={editingMessage.activeTo ?? ''}
                onChange={(e) =>
                  setEditingMessage({ ...editingMessage, activeTo: e.target.value || null })
                }
              />
            </Flex>
          </Flex>

          <Flex gap="2">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save message'}
            </Button>
            <Button variant="outline" onClick={() => setEditingMessage(null)}>
              Cancel
            </Button>
          </Flex>
        </Flex>
      )}

      <DeleteConfirmationDialog
        open={!!deletingMessage}
        title="Delete Promotional Message"
        description={`Are you sure you want to delete “${deletingMessage?.message}”? This action cannot be undone.`}
        isDeleting={deleteMutation.isPending}
        onCancel={() => setDeletingMessage(null)}
        onConfirm={() => deletingMessage && deleteMutation.mutate(deletingMessage.id)}
      />
    </Flex>
  )
}
